import React from 'react';
import { Box, Button, CircularProgress, IconButton, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import SwitchCameraOutlinedIcon from '@mui/icons-material/SwitchCameraOutlined';
import { brand, neutral, statusTone } from '../../theme';
import { formatDate } from '../../utils/format';
import { formatPriorityLabel, formatRevisionLimit } from '../../utils/projectStatus';
import StatusChip from '../ui/StatusChip';
import PriorityFlag from '../Projects/PriorityFlag';
import { WORKFLOW_STEPS, describeDeadline, projectStatusDisplay, workflowState } from './workflow';

interface WorkspaceHeaderProps {
  projectName: string;
  subtitle: string;
  status?: string | null;
  isMulticam: boolean;
  deadline?: string | null;
  priority?: string | null;
  revisionCount: number;
  revisionLimit: number;
  finished: boolean;
  tasksDone: number;
  tasksTotal: number;
  guidance: string;
  onBack: () => void;
  /** Accept / Decline while the editor still has to respond. */
  awaitingResponse: boolean;
  responding: boolean;
  onAccept?: () => void;
  onDecline?: () => void;
  canUpload: boolean;
  onUpload?: () => void;
  /** The version an upload would create (v3 → 3), for the button's tooltip. */
  nextVersion?: number;
  downloading: boolean;
  onDownloadPackage: () => void;
  /** Extra chips in the meta row (frame rate, …). */
  metaExtra?: React.ReactNode;
  /** Whose side the page is seen from: picks the status words and tones. */
  viewer?: 'creator' | 'editor' | 'unauthorized';
  /** The project has an assigned editor (an unanswered assignment reads "Awaiting your response"). */
  hasEditor?: boolean;
}

/** "1 of 2 used", or "3 used · no limit" on plans without a cap (the label says what is counted). */
const roundsUsage = (count: number, limit: number) =>
  Number.isFinite(limit) ? `${count} of ${formatRevisionLimit(limit)} used` : `${count} used · no limit`;

const STEP_COLORS = {
  done: { bg: statusTone.done.main, fg: 'common.white', border: statusTone.done.main },
  active: { bg: brand.ink, fg: 'common.white', border: brand.ink },
  attention: { bg: statusTone.attention.main, fg: 'common.white', border: statusTone.attention.main },
  upcoming: { bg: 'background.paper', fg: brand.textMuted, border: brand.borderStrong },
  skipped: { bg: neutral[200], fg: brand.textSubtle, border: neutral[300] },
} as const;

/** Readable by screen readers, hidden on screen (used below the wide breakpoint). */
const hiddenBelowLg = {
  position: { xs: 'absolute', lg: 'static' },
  width: { xs: 1, lg: 'auto' },
  height: { xs: 1, lg: 'auto' },
  overflow: { xs: 'hidden', lg: 'visible' },
  clip: { xs: 'rect(0 0 0 0)', lg: 'auto' },
} as const;

/**
 * Compact horizontal workflow stepper. Seven labelled steps need a wide
 * screen; narrower ones show the dots and name the current step underneath
 * (the labels stay readable by screen readers).
 */
const WorkflowStepper: React.FC<{ status?: string | null; revisionCount: number }> = ({ status, revisionCount }) => {
  const { activeIndex, states, cancelled } = workflowState(status, revisionCount);
  const currentIndex = Math.max(0, activeIndex);
  return (
    <Box>
      <Box
        component="ol"
        aria-label="Project workflow"
        sx={{ display: 'flex', alignItems: 'center', listStyle: 'none', m: 0, p: 0, gap: 0, minWidth: 0, width: '100%' }}
      >
        {WORKFLOW_STEPS.map((step, index) => {
          const state = cancelled ? 'skipped' : states[index];
          const colors = STEP_COLORS[state];
          const current = state === 'active' || state === 'attention';
          return (
            <Box
              component="li"
              key={step}
              aria-current={current ? 'step' : undefined}
              sx={{ position: 'relative', display: 'flex', alignItems: 'center', flex: index > 0 ? '1 1 auto' : '0 0 auto', minWidth: 0 }}
            >
              {index > 0 && (
                <Box
                  aria-hidden="true"
                  sx={{
                    flex: 1,
                    minWidth: { xs: 6, lg: 12 },
                    height: 2,
                    mx: { xs: 0.5, lg: 1 },
                    borderRadius: 1,
                    bgcolor: states[index - 1] === 'done' && !cancelled ? statusTone.done.main : neutral[300],
                  }}
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: colors.bg,
                    color: colors.fg,
                    border: '1.5px solid',
                    borderColor: colors.border,
                    '& svg': { fontSize: 14 },
                  }}
                >
                  {state === 'done' ? <CheckOutlinedIcon /> : state === 'attention' ? <PriorityHighOutlinedIcon /> : index + 1}
                </Box>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 12.5,
                    fontWeight: current ? 700 : 600,
                    color: state === 'upcoming' || state === 'skipped' ? 'text.secondary' : 'text.primary',
                    whiteSpace: 'nowrap',
                    ...hiddenBelowLg,
                  }}
                >
                  {step}
                  {state === 'skipped' && (
                    <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                      {' '}(skipped)
                    </Box>
                  )}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      <Typography
        aria-hidden="true"
        variant="body2"
        data-testid="workflow-current-step"
        sx={{ display: { xs: 'block', lg: 'none' }, mt: 1, fontWeight: 600, color: 'text.primary' }}
      >
        {cancelled ? 'Cancelled' : `Step ${currentIndex + 1} of ${WORKFLOW_STEPS.length}: ${WORKFLOW_STEPS[currentIndex]}`}
      </Typography>
    </Box>
  );
};

const MetaItem: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
    <Typography variant="caption" component="span" sx={{ color: 'text.secondary', fontWeight: 600, lineHeight: 1.5 }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 24 }}>{children}</Box>
  </Box>
);

/**
 * Workspace header: project identity and status, the workflow stepper, the
 * deadline countdown, and the actions that matter right now (accept/decline,
 * upload a revision, download the handoff package).
 */
const WorkspaceHeader: React.FC<WorkspaceHeaderProps> = ({
  projectName,
  subtitle,
  status,
  isMulticam,
  deadline,
  priority,
  revisionCount,
  revisionLimit,
  finished,
  tasksDone,
  tasksTotal,
  guidance,
  onBack,
  awaitingResponse,
  responding,
  onAccept,
  onDecline,
  canUpload,
  onUpload,
  nextVersion,
  downloading,
  onDownloadPackage,
  metaExtra,
  viewer = 'editor',
  hasEditor = true,
}) => {
  const due = describeDeadline(deadline, new Date(), finished);
  const percent = tasksTotal > 0 ? Math.round((tasksDone / tasksTotal) * 100) : 0;
  const statusDisplay = projectStatusDisplay(status, { viewer, hasEditor });
  const flagged = ['HIGH', 'URGENT'].includes(String(priority || '').toUpperCase());
  const roundsUsedUp = Number.isFinite(revisionLimit) && revisionCount >= revisionLimit;
  const dueColor = due?.tone === 'error' ? statusTone.problem.fg : due?.tone === 'warning' ? statusTone.attention.fg : 'text.secondary';

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <IconButton
          onClick={onBack}
          aria-label="Back to projects"
          sx={{ mt: 0.25, border: `1px solid ${brand.border}`, bgcolor: 'background.paper', '&:hover': { bgcolor: brand.ivory } }}
        >
          <ArrowBackOutlinedIcon />
        </IconButton>
        <Box sx={{ flex: { xs: '1 1 0', md: '1 1 320px' }, minWidth: 0 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 750, lineHeight: 1.2, letterSpacing: '-0.015em', overflowWrap: 'anywhere' }}
          >
            {projectName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1.25, rowGap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
            <Box component="span" data-testid="project-status" sx={{ display: 'inline-flex' }}>
              <StatusChip kind="project" status={status} label={statusDisplay.label} tone={statusDisplay.tone} />
            </Box>
            {isMulticam && (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem', fontWeight: 600, color: 'text.secondary' }}>
                <SwitchCameraOutlinedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
                Multicam
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {subtitle}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            width: { xs: '100%', md: 'auto' },
            mt: 0.25,
          }}
        >
          <Button
            variant="outlined"
            onClick={onDownloadPackage}
            disabled={downloading}
            startIcon={downloading ? <CircularProgress size={16} /> : <FolderZipOutlinedIcon />}
          >
            {downloading ? 'Preparing package…' : 'Download package'}
          </Button>
          {awaitingResponse && onAccept && onDecline ? (
            <>
              <Button variant="text" color="inherit" onClick={onDecline} disabled={responding}>
                Decline
              </Button>
              <Button
                variant="contained"
                onClick={onAccept}
                disabled={responding}
                startIcon={responding ? <CircularProgress size={16} color="inherit" /> : <CheckOutlinedIcon />}
              >
                Accept project
              </Button>
            </>
          ) : onUpload ? (
            <Tooltip
              title={
                finished
                  ? 'This project is finished, so no more revisions can be uploaded'
                  : nextVersion
                    ? `Upload v${nextVersion} for the creator to review`
                    : 'Upload the next version for the creator to review'
              }
            >
              <span>
                <Button
                  variant="contained"
                  onClick={onUpload}
                  disabled={!canUpload}
                  startIcon={<CloudUploadOutlinedIcon />}
                >
                  Upload revision
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: brand.border, overflow: 'hidden' }}>
        <Box sx={{ px: { xs: 1.5, md: 2.25 }, py: 1.5, borderBottom: `1px solid ${brand.border}`, bgcolor: neutral[50] }}>
          <WorkflowStepper status={status} revisionCount={revisionCount} />
        </Box>
        <Box sx={{ px: { xs: 1.5, md: 2.25 }, py: 1.5, display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ flex: '1 1 340px', minWidth: 0, color: 'text.secondary' }} data-testid="next-step">
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>Next: </Box>
            {guidance}
          </Typography>
          <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <MetaItem label="Deadline">
              {deadline ? (
                <>
                  <EventOutlinedIcon aria-hidden="true" sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatDate(deadline)}</Typography>
                  {due && (
                    <Typography variant="body2" sx={{ color: dueColor, fontWeight: due.tone === 'error' || due.tone === 'warning' ? 600 : 400, whiteSpace: 'nowrap' }}>
                      {due.relative}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">No deadline</Typography>
              )}
            </MetaItem>
            <MetaItem label="Priority">
              {flagged ? (
                <PriorityFlag priority={priority} />
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatPriorityLabel(priority)}</Typography>
              )}
            </MetaItem>
            <MetaItem label="Change rounds">
              <Typography
                variant="body2"
                data-testid="change-rounds"
                sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: roundsUsedUp ? statusTone.attention.fg : 'text.primary' }}
              >
                {roundsUsage(revisionCount, revisionLimit)}
              </Typography>
            </MetaItem>
            <MetaItem label="Tasks">
              <Box sx={{ width: 72 }}>
                <LinearProgress
                  variant="determinate"
                  value={percent}
                  aria-label={`${tasksDone} of ${tasksTotal} tasks done`}
                  sx={{ height: 6, borderRadius: 3, bgcolor: neutral[200], '& .MuiLinearProgress-bar': { bgcolor: statusTone.done.main } }}
                />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {tasksDone} of {tasksTotal} done
              </Typography>
            </MetaItem>
            {metaExtra && <MetaItem label="Timecode">{metaExtra}</MetaItem>}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default WorkspaceHeader;
